import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { SmallItemCard } from 'pages/NewDashboard/components/SmallItemCard';
import { Box, Button, Typography } from '@mui/material';
import styled from '@emotion/styled';
import {
	getClaimList,
	getUserDeposit,
	TxEventType,
	UserWithdrawDeposit,
	userWithdrawTransaction,
} from 'apis';
import { isPassed } from 'utils/lockDuration';
import { AmountBox } from 'pages/Landing/components/AmountBox';
import { ConfirmationModal } from 'pages/Landing/components/ConfirmationModal';
import { toast } from 'react-toastify';
import {
	fetchAgentPoolUserBalance,
	getAptosIndexerBalance,
} from 'utils/getAptosBalance';
import { useAptosContractBalance } from 'hooks/useAptosContractBalance';
import { useAptosWallet } from '@razorlabs/wallet-kit';
import { APTOS_LOCKING_PERIOD, CONTRACT_TOKEN } from 'data/constant';
import { ClaimModal } from 'pages/Landing/components/ClaimModal';

const Column = styled('div')`
	display: flex;
	flex-direction: column;
`;

const SpaceBetween = styled(Column)`
	height: 100%;
	justify-content: space-between;
`;

const StyledButton = styled(Button)<{ disabled?: boolean }>`
	display: flex;
	padding: 12px 32px;
	box-shadow: 0 4px 4px transparent;
	border-radius: 8px;
	overflow: hidden;
	background-color: ${({ disabled }) => (disabled ? '#dadada' : '#541d18')};

	:hover {
		background-color: ${({ disabled }) =>
			disabled ? '#dadada' : '#bb805f'};
		cursor: ${({ disabled }) => (disabled ? 'no-drop' : 'pointer')};
		box-shadow: 0 4px 4px transparent;
	}
`;

export const WithdrawPanel: React.ComponentType<{ onSuccess?(): void }> = ({
	onSuccess,
}) => {
	const [open, setOpen] = useState(false);
	const [userDeposits, setUserDeposits] = useState();
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [confirmationData, setConfirmationData] = useState({});
	const [claims, setClaims] = useState<UserWithdrawDeposit[]>([]);
	const [loading, setLoading] = useState(false);
	const [balance, setBalance] = useState('0');
	const [contractBal, setContractBal] = useState('0');
	const [amount, setAmount] = useState('');
	const { connected } = useAptosWallet();

	const appVault = useSelector((state: RootState) => state.vault);
	const selectedVault = useSelector((state: RootState) => state.vault);
	const selectedContract = selectedVault.exchanger.find(
		(d) => d.value === appVault.exchange,
	);
	const { aptosAccount, signAndSubmitTransaction } =
		useAptosContractBalance();
	const canWithdraw = claims.some((item) => item.claimStatus === 'processed');
	const claimProcessing = claims.some(
		(item) => item.claimStatus === 'processing',
	);

	const bal = parseFloat(parseFloat(balance).toFixed(2)).toLocaleString();
	const contractBalance = parseFloat(
		parseFloat(contractBal).toFixed(2),
	).toLocaleString();
	const payload = useMemo(() => {
		return {
			contract: selectedContract?.contractAddress || '',
			address: aptosAccount?.address || '',
		};
	}, [aptosAccount?.address, selectedContract?.contractAddress]);

	const buttonLabel = useMemo(() => {
		if (!connected) return 'Connect Wallet';

		if (loading) return 'Please wait...';

		if (canWithdraw) {
			return 'Withdraw';
		}

		if (claimProcessing) return 'Claim is under processing';

		return 'Claim';
	}, [connected, loading, canWithdraw, claimProcessing]);

	const getAptosAccountBalance = useCallback(async () => {
		const res = await getAptosIndexerBalance(aptosAccount?.address || '');
		setBalance(res);
	}, [aptosAccount?.address]);

	const fetchClaims = useCallback(async () => {
		try {
			if (aptosAccount?.address) {
				const res = await getClaimList(
					selectedContract?.contractAddress || '',
					aptosAccount?.address || '',
				);

				setClaims(res);
			}
		} catch (e) {
			//
		}
	}, [aptosAccount?.address, selectedContract?.contractAddress]);

	const getAptosContractBalance = useCallback(async () => {
		const res = await fetchAgentPoolUserBalance(
			selectedContract?.contractAddress || '',
			aptosAccount?.address || '',
		);
		setContractBal(res);
	}, [aptosAccount?.address, selectedContract?.contractAddress]);

	const onWithDraw = async () => {
		try {
			await userWithdrawTransaction({
				contract: selectedContract?.contractAddress || '',
				address: aptosAccount?.address || '',
				amount: contractBalance,
				token: CONTRACT_TOKEN,
				eventType: TxEventType.WITHDRAW,
			});
			await fetchClaims();
		} catch (e) {
			//
		}
	};

	const onAptosWithdraw = async () => {
		try {
			setLoading(true);
			const res = await getUserDeposit(payload);
			if (res.lastDeposit.length > 0) {
				const isLocked = isPassed(
					res.lastDeposit[0].createdAt,
					APTOS_LOCKING_PERIOD,
				);
				if (isLocked.isLocked) {
					toast(
						`You can withdraw after ${isLocked.newTime.format(
							'YYYY-MM-DD HH:mm:ss',
						)}`,
					);
					setConfirmationOpen(false);
					return;
				}
				if (selectedContract && selectedContract.contractAddress) {
					const response = await signAndSubmitTransaction({
						payload: {
							function: `${selectedContract?.contractAddress}::agent_pool::withdraw`,
							typeArguments: [
								'0x1::aptos_coin::AptosCoin',
								//'0xa', //'0xa::aptos_coin::AptosCoin',
							],
							functionArguments: [
								(parseFloat(contractBalance) * 100000000)
									.toFixed(0)
									.toString(),
								aptosAccount?.address,
							],
						},
					});
					setConfirmationOpen(false);
					await new Promise((resolve) => setTimeout(resolve, 8000));
					if (response.status === 'Approved') {
						toast(
							'Withdraw successful' || {
								type: 'success',
							},
						);
						onWithDraw();
						onSuccess?.();
					}
				}
			}
			getAptosContractBalance();
			getAptosAccountBalance();
		} catch (e: any) {
			toast('Something went wrong', {
				type: 'error',
			});
		} finally {
			setLoading(false);
		}
	};
	const withdrawTransaction = async (confirData: UserWithdrawDeposit) => {
		try {
			await userWithdrawTransaction(confirData);

			await fetchClaims();
			setConfirmationOpen(false);
			setOpen(false);
		} catch (e) {
			//
		}
	};

	const fetchLockedTime = useCallback(async () => {
		const res = await getUserDeposit(payload);
		if (res.lastDeposit.length > 0) {
			setUserDeposits(res.lastDeposit[0].createdAt);
		}
	}, [payload]);

	useEffect(() => {
		getAptosContractBalance();
		getAptosAccountBalance();
		fetchLockedTime();
	}, [fetchLockedTime, getAptosAccountBalance, getAptosContractBalance]);

	useEffect(() => {
		getAptosContractBalance();
		getAptosAccountBalance();
		fetchLockedTime();
	}, [getAptosAccountBalance, getAptosContractBalance, fetchLockedTime]);

	useEffect(() => {
		fetchClaims();
	}, [fetchClaims]);

	return (
		<>
			<SpaceBetween>
				<Column>
					<SmallItemCard />
					<Box height={16} />
					<AmountBox
						currency={{
							icon: selectedVault.token[0].icon,
							name: selectedVault.token[0].name,
						}}
						text={amount}
						leftTxt={`${selectedVault.currency} ${bal}`}
						rightTxt={
							selectedVault.currency + ' ' + contractBalance
						}
					/>

					<Box height={20} />
				</Column>
				<Column>
					<StyledButton
						variant={'contained'}
						onClick={() => {
							if (canWithdraw) {
								setConfirmationOpen(true);
							} else {
								setOpen(true);
							}
						}}
						disabled={
							loading || //isPassed
							parseFloat(contractBal) === 0 ||
							(claims.length === 0 ? false : !canWithdraw)
						}
					>
						<Typography fontWeight={600} color={'#fff'}>
							{buttonLabel}
						</Typography>
					</StyledButton>
					<Box height={32} />
				</Column>
			</SpaceBetween>
			<ConfirmationModal
				open={confirmationOpen}
				onClose={() => setConfirmationOpen(false)}
				onConfirm={() => {
					if (parseFloat(contractBalance) > 0) {
						onAptosWithdraw();
						setConfirmationOpen(false);
					}
				}}
				info={confirmationData as UserWithdrawDeposit}
			/>
			<ClaimModal
				open={open}
				setClose={() => {
					setOpen(false);
				}}
				onSuccess={() => {
					setOpen(false);
					withdrawTransaction({
						contract: selectedContract?.contractAddress || '',
						address: aptosAccount?.address || '',
						amount: contractBalance,
						token: CONTRACT_TOKEN,
						eventType: TxEventType.CLAIM,
					});
				}}
			/>
		</>
	);
};
